import React, { useState, useEffect } from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis,
  Radar, ResponsiveContainer
} from 'recharts';
import './Sidebar.css';

const API = "/api";

function tierColor(tier) {
  if (!tier) return 'var(--text-secondary)';
  const t = tier.toLowerCase();
  if (t.includes('elite') || t.includes('strong') || t.includes('durable') || t.includes('low risk'))
    return 'var(--risk-low)';
  if (t.includes('balanced') || t.includes('moderate') || t.includes('solid') || t.includes('elevated'))
    return 'var(--risk-elevated)';
  return 'var(--risk-high)';
}

function SignalCard({ label, value, tier }) {
  const display = value && value !== 'not available' ? Number(value).toFixed(1) : '—';
  return (
    <div className="signal-card">
      <div className="signal-header">
        <span className="signal-dot" style={{ background: tierColor(tier) }} />
        <span className="signal-label">{label}</span>
      </div>
      <div className="signal-value">{display}</div>
      <div className="signal-tier">{tier || '—'}</div>
    </div>
  );
}

export default function Sidebar({ player, profile, mobileOpen, onMobileClose }) {
  const [radarData, setRadarData] = useState(null);

  useEffect(() => {
    if (!player) { setRadarData(null); return; }
    const name = player.split(' (')[0];
    const tour = player.includes('(')
      ? player.split('(')[1].replace(')', '')
      : 'ATP';
    fetch(
      `${API}/player/${encodeURIComponent(name)}/chart/radar?season=2024&tour=${tour}`
    )
      .then(r => r.json())
      .then(d => { if (d.dimensions) setRadarData(d.dimensions); })
      .catch(() => {});
  }, [player]);

  if (!player || !profile) {
    return (
      <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
        <button className="sidebar-close-mobile" onClick={onMobileClose}>✕ chiudi</button>
        <div className="sidebar-empty">
          <svg width="56" height="78" viewBox="0 0 100 140">
            <ellipse cx="50" cy="55" rx="36" ry="43" fill="none" stroke="#AFA9EC" strokeWidth="1.5" opacity="0.10"/>
            <ellipse cx="50" cy="55" rx="23" ry="30" fill="none" stroke="#AFA9EC" strokeWidth="1.2" opacity="0.15"/>
            <ellipse cx="50" cy="55" rx="14" ry="19" fill="none" stroke="#AFA9EC" strokeWidth="1" opacity="0.25"/>
            <ellipse cx="50" cy="55" rx="9" ry="11" fill="none" stroke="#CECBF6" strokeWidth="0.8" opacity="0.35"/>
            <ellipse cx="50" cy="55" rx="5" ry="7" fill="#EEEDFE" opacity="0.1"/>
            <ellipse cx="49" cy="53" rx="2" ry="2" fill="white" opacity="0.15"/>
          </svg>
          <span>Chiedi di un giocatore</span>
        </div>
      </aside>
    );
  }

  const name = player.split(' (')[0];
  const tour = player.includes('(') ? player.split('(')[1].replace(')', '') : '';

  return (
    <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
      <button className="sidebar-close-mobile" onClick={onMobileClose}>✕ chiudi</button>
      <div className="sidebar-header">
        <div className="player-name">{name}</div>
        <span className="tour-badge">{tour}</span>
      </div>

      <div className="signals">
        <SignalCard
          label="Competitive"
          value={profile.competitive_signal}
          tier={profile.competitive_tier}
        />
        <SignalCard
          label="Risk"
          value={profile.risk_score}
          tier={profile.risk_tier}
        />
        <SignalCard
          label="Durability"
          value={profile.durability_profile}
          tier={profile.durability_tier}
        />
      </div>

      {radarData && (
        <div className="sidebar-radar">
          <div className="sidebar-section-label">PHYSICAL PROFILE</div>
          <ResponsiveContainer width="100%" height={180}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="var(--border)" />
              <PolarAngleAxis
                dataKey="axis"
                tick={{ fontSize: 9, fill: 'var(--text-secondary)' }}
              />
              <Radar
                dataKey="value"
                stroke="var(--accent-bright)"
                fill="var(--accent-bright)"
                fillOpacity={0.25}
                strokeWidth={1.5}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="sidebar-badges">
        {profile.context_signal_available_flag === 1 && (
          <span className="badge badge-context">Context Available</span>
        )}
        {profile.market_signal_available_flag === 1 && (
          <span className="badge badge-market">
            Market: {profile.market_sample_tier || 'Available'}
          </span>
        )}
        {profile.market_signal_available_flag === 0 && (
          <span className="badge badge-no-market">No Market Data</span>
        )}
      </div>
    </aside>
  );
}
